import './ApiToken.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import copyToClipboard from 'copy-text-to-clipboard';

import {
  Button,
  Header,
  InfoBlock,
  Input,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { Api } from '$api';
import { useTranslator } from '$hooks';
import { Loader } from '$shared/components';
import { IcCopy28, IcDelete24 } from '$assets';
import { toastActions } from '$store/toast';
import { mainSelector } from '$store/main';
import { _confirm, format } from '$utils';
import { goTo } from '$navigation/helper';
import { IM } from '$shared/constants/pages';

type ApiTokenItem = {
  id: number;
  name: string;
  createdAt: number; // unix milliseconds
  token?: string;
};

type ListResponse = { tokens: ApiTokenItem[] };

const ENDPOINT = '/profile/bot_api_tokens';

interface ApiTokenProps {
  userId?: number | string;
}

export const ApiToken: FC<ApiTokenProps> = ({ userId }) => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { mVerified } = useSelector(mainSelector);

  const isEmbedded = !!userId;
  const query = userId ? `?userID=${userId}` : '';

  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [tokens, setTokens] = useState<ApiTokenItem[]>([]);
  const [name, setName] = useState('');
  const [revealed, setRevealed] = useState<{ token: string; isNew: boolean } | null>(
    null,
  );
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    if (!isEmbedded && !mVerified) {
      goTo(IM);
    }
  }, [isEmbedded, mVerified]);

  const loadData = useCallback(() => {
    setLoadingStatus('loading');
    Api.get<any, ListResponse>(`${ENDPOINT}${query}`)
      .then((data) => {
        setTokens(data.tokens || []);
        setLoadingStatus('');
      })
      .catch(() => {
        setLoadingStatus('failed');
      });
  }, [query]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const issueToken = useCallback(() => {
    if (isBusy) {
      return;
    }
    setIsBusy(true);
    dispatch(toastActions.setToastLoading());
    Api.post<any, ApiTokenItem>(`${ENDPOINT}/issue`, {
      name: name.trim(),
      ...(userId ? { peerID: Number(userId) } : {}),
    })
      .then((data) => {
        setRevealed({ token: data.token || '', isNew: true });
        setName('');
        dispatch(toastActions.hideToast());
        loadData();
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message || t('api_token_generate_error')));
      })
      .finally(() => {
        setIsBusy(false);
      });
  }, [dispatch, isBusy, loadData, name, t, userId]);

  const revealToken = useCallback(
    (id: number) => {
      if (isBusy) {
        return;
      }
      setIsBusy(true);
      dispatch(toastActions.setToastLoading());
      Api.get<any, ApiTokenItem>(`${ENDPOINT}/${id}${query}`)
        .then((data) => {
          setRevealed({ token: data.token || '', isNew: false });
          dispatch(toastActions.hideToast());
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message || t('api_token_reveal_error')));
        })
        .finally(() => {
          setIsBusy(false);
        });
    },
    [dispatch, isBusy, query, t],
  );

  const revokeToken = useCallback(
    (id: number) => {
      if (isBusy) {
        return;
      }
      _confirm(t('api_token_revoke_confirm'), () => {
        setIsBusy(true);
        dispatch(toastActions.setToastLoading());
        Api.delete(`${ENDPOINT}/${id}${query}`)
          .then(() => {
            dispatch(toastActions.setToastSuccess(t('api_token_revoke_success')));
            loadData();
          })
          .catch((e) => {
            dispatch(toastActions.setToastFail(e.message || t('api_token_revoke_error')));
          })
          .finally(() => {
            setIsBusy(false);
          });
      });
    },
    [dispatch, isBusy, loadData, query, t],
  );

  const handleCopy = useCallback(
    (value: string) => {
      if (!value) {
        return;
      }
      if (copyToClipboard(value)) {
        dispatch(toastActions.setToastSuccess(t('copied')));
      } else {
        dispatch(toastActions.setToastFail(t('api_token_copy_failed')));
      }
    },
    [dispatch, t],
  );

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (revealed) {
      return (
        <div className="ApiToken__cont">
          <InfoBlock
            type={revealed.isNew ? 'success' : 'info'}
            title={
              revealed.isNew ? t('api_token_created_title') : t('api_token_reveal_title')
            }
          >
            {revealed.isNew ? t('api_token_created_hint') : t('api_token_reveal_hint')}
          </InfoBlock>
          <div className="ApiToken__tokenWrap" onClick={() => handleCopy(revealed.token)}>
            <Input value={revealed.token} disabled multiline />
            <div className="ApiToken__tokenCopy">
              <IcCopy28 />
            </div>
          </div>
          <Button onClick={() => handleCopy(revealed.token)}>
            {t('api_token_copy_button')}
          </Button>
          <Button mode="secondary" onClick={() => setRevealed(null)}>
            {t('done')}
          </Button>
        </div>
      );
    }

    return (
      <div className="ApiToken__cont">
        <InfoBlock type="info" title={t('api_token_title')}>
          {t('api_token_description')}
        </InfoBlock>

        <div className="ApiToken__create">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('api_token_name_placeholder')}
            maxLength={255}
          />
          <Button onClick={issueToken} disabled={isBusy}>
            {t('api_token_create_button')}
          </Button>
        </div>

        {tokens.length > 0 ? (
          <List title={t('api_token_list_title')}>
            {tokens.map((tok) => (
              <ListItem
                key={tok.id}
                description={format(new Date(tok.createdAt), 'd MMM yyyy')}
                onClick={() => revealToken(tok.id)}
                after={
                  <div
                    className="ApiToken__revoke"
                    onClick={(e) => {
                      e.stopPropagation();
                      revokeToken(tok.id);
                    }}
                  >
                    <IcDelete24 />
                  </div>
                }
              >
                {tok.name || `${t('api_token_unnamed')} #${tok.id}`}
              </ListItem>
            ))}
          </List>
        ) : (
          <div className="ApiToken__empty">{t('api_token_no_token')}</div>
        )}
      </div>
    );
  }

  if (isEmbedded) {
    return renderContent();
  }

  return (
    <>
      <Header showBackButton>{t('api_token_title')}</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
